import { useEffect, lazy, Suspense } from "react";
import { preload } from "react-dom";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { LazyMotion } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/Layout";
import { trackPageView } from "@/lib/tracking";
import djDeck640 from "@/assets/dj-deck-640.webp";
import djDeck1024 from "@/assets/dj-deck-1024.webp";
import djDeck1600 from "@/assets/dj-deck-1600.webp";
import crowd640 from "@/assets/crowd-640.webp";
import crowd1024 from "@/assets/crowd-1024.webp";
import crowd1600 from "@/assets/crowd-1600.webp";

// Home is statically imported so the most-visited landing page renders without
// an extra chunk round-trip (keeps LCP fast). Every other route — especially
// the large admin dashboard — is code-split so public pages download only the
// JS they need.
import Home from "@/pages/Home";
const About = lazy(() => import("@/pages/About"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const Partnerships = lazy(() => import("@/pages/Partnerships"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const NotFound = lazy(() => import("@/pages/not-found"));

const queryClient = new QueryClient();

// Animation features are fetched async so the framer-motion engine (~100KB+)
// stays out of the critical entry bundle; `m.*` components render instantly
// and pick up animations when this chunk lands.
const loadMotionFeatures = () =>
  import("@/lib/motionFeatures").then((mod) => mod.domAnimation);

function PageViewTracker() {
  const [location] = useLocation();
  useEffect(() => {
    trackPageView(location || "/");
  }, [location]);
  return null;
}

// Route → LCP hero image map. Lazy-loaded routes only start downloading their
// hero once the route chunk has parsed; preloading from the (eager) app shell
// lets the image download in parallel with the chunk. Home preloads its own
// hero (it is statically imported), and Partnerships has a CSS-only hero.
const HERO_PRELOADS: Record<string, { src: string; srcSet: string }> = {
  "/events": {
    src: djDeck1600,
    srcSet: `${djDeck640} 640w, ${djDeck1024} 1024w, ${djDeck1600} 1600w`,
  },
  "/about": {
    src: crowd1600,
    srcSet: `${crowd640} 640w, ${crowd1024} 1024w, ${crowd1600} 1600w`,
  },
};

function HeroPreloader() {
  const [location] = useLocation();
  const hero = HERO_PRELOADS[location];
  if (hero) {
    preload(hero.src, {
      as: "image",
      imageSrcSet: hero.srcSet,
      imageSizes: "100vw",
      fetchPriority: "high",
    });
  }
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function Router() {
  return (
    <Suspense
      fallback={
        // Contentful fallback: paint the shared shell (nav + footer) instead of
        // a blank screen while a route chunk downloads, so first paint happens
        // before the page's JS arrives.
        <Layout>
          <div className="hero-bg min-h-[60vh]" aria-hidden="true" />
        </Layout>
      }
    >
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/events" component={Events} />
        <Route path="/events/:slug" component={EventDetail} />
        <Route path="/partnerships" component={Partnerships} />
        <Route path="/contact" component={Contact} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin" component={AdminDashboard} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={loadMotionFeatures} strict>
        <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <ScrollToTop />
          <PageViewTracker />
          <HeroPreloader />
          <Router />
        </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </LazyMotion>
    </QueryClientProvider>
  );
}

export default App;
